import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [autoAcceptTried, setAutoAcceptTried] = useState(false); // Track if we've attempted auto-accept

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Store token in localStorage for post-auth flow
    localStorage.setItem('pendingInvitationToken', token);

    // Fetch invitation details using the secure edge function
    const fetchInvitation = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-invitation-details', {
          body: { token }
        });

        if (error) {
          throw new Error(error.message || 'Failed to load invitation');
        }

        if (data.error) {
          setError(data.error);
          return;
        }

        setInvitation(data.invitation);
      } catch (err: any) {
        console.error('Error fetching invitation:', err);
        setError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Auto-accept invitation after user logs in (only once)
  useEffect(() => {
    const storedToken = localStorage.getItem('pendingInvitationToken');
    // Only auto-accept if we've not tried already, have invitation data, and there is no error
    if (user && storedToken && invitation && !accepting && !autoAcceptTried && !error) {
      setAutoAcceptTried(true);
      acceptInvitation();
    }
  }, [user, invitation, accepting, autoAcceptTried, error]);

  const acceptInvitation = async () => {
    if (!user || !invitation) {
      // Redirect to login if not authenticated
      const currentUrl = window.location.pathname + window.location.search;
      navigate(`/auth?redirect=${encodeURIComponent(currentUrl)}`);
      return;
    }

    setAccepting(true);
    // Clear pending token on any attempt (avoid infinite retries)
    localStorage.removeItem('pendingInvitationToken');

    try {
      // Call the secure edge function to accept invitation
      const { data, error } = await supabase.functions.invoke('accept-invitation', {
        body: {
          token: invitation.token,
          userId: user.id
        }
      });

      // If edge function returns a non-2XX, error will be present, but message will not be detailed
      if (error || data?.error) {
        // Prefer the detailed message from the body
        const detail = data?.error || error?.message || 'Failed to accept invitation';
        // Also log the full output for debugging
        console.error('[AcceptInvitation] accept-invitation returned error:', { error, data });
        throw new Error(detail);
      }

      toast({
        title: "Success!",
        description: `You've joined "${data.boardTitle}" as a ${data.role}`,
      });

      // Redirect to the board
      navigate('/');
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      // Prefer the specific error for the UI as well
      setError(err.message || "Failed to accept invitation");
      toast({
        title: "Error",
        description: err.message || "Failed to accept invitation",
        variant: "destructive"
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading invitation...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <CardTitle>Board Invitation</CardTitle>
          <CardDescription>
            You've been invited to join "{invitation?.board?.title}"
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {invitation?.board?.description && (
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {invitation.board.description}
              </p>
            </div>
          )}
          
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Role: <span className="font-medium capitalize">{invitation?.role}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Expires: {new Date(invitation?.expires_at).toLocaleDateString()}
            </p>
          </div>

          {!user ? (
            <div className="space-y-2">
              <p className="text-sm text-center text-gray-600 dark:text-gray-400">
                Please sign in or create an account to accept this invitation
              </p>
              <Button 
                onClick={() => {
                  const currentUrl = window.location.pathname + window.location.search;
                  navigate(`/auth?redirect=${encodeURIComponent(currentUrl)}`);
                }}
                className="w-full"
              >
                Sign In / Sign Up
              </Button>
            </div>
          ) : (
            <Button 
              onClick={acceptInvitation} 
              disabled={accepting}
              className="w-full"
            >
              {accepting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Accepting...
                </>
              ) : (
                'Accept Invitation'
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
